// Client-side authentication via Firebase Auth / Google Cloud Identity Platform.
// Sign-in/out helpers + auth-state subscription. The shared App/Auth instance
// lives in ../firebase/client. The tenancy model rides in the user's custom
// claims ({ tenantId, role }), set server-side by the onUserCreate blocking
// function; we read them off the ID token here.

import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { auth } from '../firebase/client';

const fns = getFunctions(app, 'us-central1');
const acceptInviteFn = httpsCallable(fns, 'acceptInvite');
const startTrialFn = httpsCallable(fns, 'startTrial');
const createPortalSessionFn = httpsCallable(fns, 'createPortalSession');
const reactivateFn = httpsCallable(fns, 'reactivate');
const acceptTermsFn = httpsCallable(fns, 'acceptTerms');
const sendVerificationLinkFn = httpsCallable(fns, 'sendVerificationLink');
const sendPasswordResetFn = httpsCallable(fns, 'sendPasswordReset');
const assistantChatFn = httpsCallable(fns, 'assistantChat');

// Ask the in-app help assistant. `messages` is [{role, content}]; returns { text }.
export async function askAssistant(messages) {
  const res = await assistantChatFn({ messages });
  return res.data;
}

// Ask the server to email a verification link via Resend (not Firebase's
// built-in mail). The signed-in user's token identifies the recipient.
export async function requestVerification() {
  const res = await sendVerificationLinkFn();
  return res.data;
}

// Record the signed-in member's acceptance of the current Terms of Service.
export async function acceptTerms() {
  const res = await acceptTermsFn();
  return res.data;
}

// Start a trial: returns { url } to redirect to Stripe Checkout (card up front),
// or { provisioned: true } when the account is comped (facility created directly).
// Requires the admin to have accepted the Terms of Service.
export async function startTrial(name, acceptTerms) {
  const res = await startTrialFn({ name, acceptTerms: !!acceptTerms });
  return res.data;
}

// Admin: get a Stripe billing-portal URL to manage the card / subscription.
export async function openBillingPortal() {
  const res = await createPortalSessionFn();
  return res.data; // { url }
}

// Admin: reactivate a lapsed (canceled/unpaid) subscription. Returns { url } to
// redirect to a fresh Stripe Checkout for the existing customer.
export async function reactivateSubscription() {
  const res = await reactivateFn();
  return res.data; // { url }
}

const googleProvider = new GoogleAuthProvider();
const microsoftProvider = new OAuthProvider('microsoft.com');

// prompt=select_account always shows the account chooser (so an invitee on a
// shared browser can't silently reuse someone else's cached session). A
// loginHint pre-selects the invited address.
function oauthParams(loginHint) {
  return loginHint ? { prompt: 'select_account', login_hint: loginHint } : { prompt: 'select_account' };
}

export function signInWithGoogle(loginHint) {
  googleProvider.setCustomParameters(oauthParams(loginHint));
  return signInWithPopup(auth, googleProvider);
}

export function signInWithMicrosoft(loginHint) {
  microsoftProvider.setCustomParameters(oauthParams(loginHint));
  return signInWithPopup(auth, microsoftProvider);
}

// --- Email / password ----------------------------------------------------
export function signInWithEmail(email, password) {
  return signInWithEmailAndPassword(auth, email, password);
}

// Create an account. The onUserCreate blocking function runs during this call:
// with a matching invite it succeeds (and gets tenant claims); without one it
// throws and no account is created. Best-effort verification email on success.
export async function signUpWithEmail(email, password, name) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  // Set the display name (shown as their caregiver identifier), if provided.
  if (name && name.trim()) {
    try { await updateProfile(cred.user, { displayName: name.trim() }); } catch (_) { /* non-fatal */ }
  }
  // Deliver the verification link via Resend (server-side). Non-fatal on error;
  // the user can hit "Resend verification" which calls the same path.
  try { await requestVerification(); } catch (_) { /* non-fatal */ }
  return cred;
}

// Set the signed-in user's display name (shown as their caregiver identifier).
export function setDisplayName(name) {
  if (!auth.currentUser) throw new Error('Not signed in.');
  return updateProfile(auth.currentUser, { displayName: (name || '').trim() });
}

// Request a password-reset email (delivered via Resend, server-side).
// Enumeration-safe: resolves regardless of whether the account exists.
export function resetPassword(email) {
  return sendPasswordResetFn({ email });
}

// Re-send the verification email (via Resend, server-side) to the signed-in user.
export function resendVerification() {
  if (!auth.currentUser) throw new Error('Not signed in.');
  return requestVerification();
}

export function signOut() {
  return fbSignOut(auth);
}

// Read tenancy claims off the current ID token. forceRefresh picks up claims
// newly set server-side.
export async function getClaims({ forceRefresh = false } = {}) {
  const user = auth.currentUser;
  if (!user) return null;
  const { claims } = await user.getIdTokenResult(forceRefresh);
  return { uid: user.uid, email: user.email, emailVerified: user.emailVerified, displayName: user.displayName, tenantId: claims.tenantId, role: claims.role };
}

// Subscribe to auth changes. Calls cb with the claims object (or null when
// signed out). Returns the unsubscribe function.
export function onAuthChange(cb) {
  return onAuthStateChanged(auth, async (user) => {
    if (!user) { cb(null); return; }
    let res = await user.getIdTokenResult();
    // Finish provisioning an invited account as soon as its email is verified,
    // WITHOUT a second sign-in. The catch: after the user clicks the emailed
    // verification link in another tab, this tab's cached user/token still say
    // emailVerified=false. So when there's no tenant claim, reload the account +
    // force-refresh the token first, so a plain page reload reflects the
    // verification; then redeem the invite server-side and refresh again to pick
    // up the new { tenantId, role } claims.
    if (!res.claims.tenantId) {
      try {
        await user.reload();
        res = await user.getIdTokenResult(true);
        if (!res.claims.tenantId && res.claims.email_verified) {
          await acceptInviteFn();
          res = await user.getIdTokenResult(true);
        }
      } catch (_) { /* no invite / not verified -> remain tenant-less */ }
    }
    const c = res.claims;
    cb({ uid: user.uid, email: user.email, emailVerified: c.email_verified ?? user.emailVerified, displayName: user.displayName, tenantId: c.tenantId, role: c.role });
  });
}

export { auth };
